import { useState } from "react";
import { genreOptions, regionOptions, yearOptions, sortOptions } from "@/mocks/categories";

interface MovieFilterBarProps {
  genre: string;
  region: string;
  year: string;
  sort: string;
  onChange: (patch: { genre?: string; region?: string; year?: string; sort?: string }) => void;
  onReset: () => void;
}

function PillGroup({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: string[];
  value: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-label text-xs text-foreground-500">{label}</span>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onSelect(opt)}
          className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors cursor-pointer ${
            value === opt
              ? "bg-primary-500 text-background-50"
              : "bg-background-100 text-foreground-700 hover:bg-background-200"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export default function MovieFilterBar({ genre, region, year, sort, onChange, onReset }: MovieFilterBarProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3.5 md:hidden cursor-pointer"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2 font-label text-sm text-foreground-800">
          <i className="ri-filter-3-line text-lg text-primary-600" aria-hidden="true"></i>
          筛选与排序
        </span>
        <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true"></i>
      </button>

      <div className={`${expanded ? "block" : "hidden"} md:block`}>
        <div className="space-y-4 border-t border-background-200 p-4 md:border-t-0 md:p-5">
          <PillGroup
            label="类型"
            options={["全部", ...genreOptions]}
            value={genre}
            onSelect={(v) => onChange({ genre: v })}
          />
          <PillGroup
            label="地区"
            options={["全部", ...regionOptions]}
            value={region}
            onSelect={(v) => onChange({ region: v })}
          />
          <PillGroup
            label="年份"
            options={yearOptions}
            value={year}
            onSelect={(v) => onChange({ year: v })}
          />

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-label text-xs text-foreground-500">排序</span>
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onChange({ sort: opt.key })}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors cursor-pointer ${
                    sort === opt.key
                      ? "bg-accent-500 text-primary-950"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 font-label text-xs text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重置筛选
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}